// 🏆 PREMIUM CERTIFICATE EXPORT UTILITIES
// Extracted from admin CertificatesNew.tsx to reuse the exact same beautiful design

import { buildCertificateFilename, certificateHasMeaningfulWeight } from './certificateFilename';

// Load ExcelJS library for professional Excel generation with full styling
export const loadExcelJSLibrary = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if ((window as any).ExcelJS) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.4.0/exceljs.min.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load ExcelJS library'));
    document.head.appendChild(script);
  });
};

// Check if certificate data contains QA/QC information
const hasQAQCData = (certificateData: any): boolean => {
  if (certificateData.certificate_type !== 'BATCH') return false;
  if (!certificateData.samples || certificateData.samples.length === 0) return false;
  
  const firstSample = certificateData.samples[0];
  const metadonnees = firstSample?.sample_data?.metadonnees || firstSample?.metadonnees;
  
  const qaqcData = metadonnees?.qaqc_data;
  const hasData = qaqcData?.reference_standards && 
                  Array.isArray(qaqcData.reference_standards) && 
                  qaqcData.reference_standards.length > 0;
  
  return !!hasData;
};

// Generate QA/QC worksheet for SAA certificates
const generateQAQCWorksheet = (workbook: any, certificateData: any, ExcelJS: any) => {
  const firstSample = certificateData.samples[0];
  const metadonnees = firstSample?.sample_data?.metadonnees || firstSample?.metadonnees;
  const qaqcData = metadonnees?.qaqc_data;
  
  if (!qaqcData?.reference_standards) return;
  
  const qaqcSheet = workbook.addWorksheet('QAQC');
  
  // Zgounder element order: Ag, Cu, Pb, Fe, Zn (then others).
  const knownElements = ['Ag', 'Cu', 'Fe', 'Pb', 'Zn', 'Cd', 'Co', 'Mn', 'Ni', 'Ag_FA', 'Au_FA', 'Mo_ICP', 'As_ICP', 'Sn_ICP'];
  const detectedElements = new Set<string>();
  qaqcData.reference_standards.forEach((std: any) => {
    Object.keys(std).forEach((key: string) => {
      if (key !== 'customers_ref' && key !== 'lab_ref' && knownElements.includes(key)) {
        detectedElements.add(key);
      }
    });
  });
  const allElements = detectedElements.size > 0 
    ? knownElements.filter(el => detectedElements.has(el))
    : ['Ag', 'Cu', 'Fe', 'Pb', 'Zn'];

  const elementUnitsMap: Record<string, string> = {
    'Ag': 'g/t', 'Cu': '%', 'Fe': '%', 'Pb': '%', 'Zn': '%',
    'Cd': '%', 'Co': '%', 'Mn': '%', 'Ni': '%',
    'Ag_FA': 'g/t', 'Au_FA': 'g/t',
    'Mo_ICP': 'ppm', 'As_ICP': 'ppm', 'Sn_ICP': 'ppm'
  };
  const elementUnits = elementUnitsMap;
  const methodMap: Record<string, string> = {
    'Ag': 'MO/ANA/002', 'Cu': 'MO/ANA/002', 'Fe': 'MO/ANA/002', 'Pb': 'MO/ANA/002', 'Zn': 'MO/ANA/002',
    'Cd': 'MO/ANA/002', 'Co': 'MO/ANA/002', 'Mn': 'MO/ANA/002', 'Ni': 'MO/ANA/002',
    'Ag_FA': 'MO/ANA/035', 'Au_FA': 'MO/ANA/001', 'Mo_ICP': 'MO/ANA/038', 'As_ICP': 'MO/ANA/038', 'Sn_ICP': 'MO/ANA/038'
  };
  const limitsMap: Record<string, string> = {
    'Ag': '1', 'Cu': '0,01', 'Fe': '0,01', 'Pb': '0,01', 'Zn': '0,01',
    'Cd': '0,01', 'Co': '0,01', 'Mn': '0,01', 'Ni': '0,01',
    'Ag_FA': '200', 'Au_FA': '0,05', 'Mo_ICP': '1', 'As_ICP': '1', 'Sn_ICP': '1'
  };
  const methodHeaders = allElements.map(el => methodMap[el] || 'MO/ANA/002');
  const limitHeaders = allElements.map(el => limitsMap[el] || '?');

  // Build lookup: lab_ref (sample_id) -> real external_id (customers_ref)
  const sampleExternalIdByLabRef: Record<string, string> = {};
  (certificateData.samples || []).forEach((s: any) => {
    const sid = s?.sample_id || s?.sample_data?.sample_id;
    const ext = s?.external_id || s?.sample_data?.external_id;
    if (sid) sampleExternalIdByLabRef[String(sid).trim()] = ext ? String(ext) : '';
  });

  // Unit-aware formatter for QA/QC values (mirrors main results table)
  const formatQaqcValue = (rawValue: any, element: string): string => {
    if (rawValue === null || rawValue === undefined) return '';
    const strVal = String(rawValue).trim();
    if (strVal === '') return '';
    const num = parseFloat(strVal.replace(',', '.'));
    if (Number.isNaN(num)) return strVal;
    const unit = elementUnits[element];
    if (unit === '%') {
      const pct = num / 10000;
      if (pct < 0.01) return '<0.01';
      return pct.toFixed(2);
    }
    if (element === 'Au_FA') {
      if (num < 0.05) return '<0.05';
      return num.toFixed(2);
    }
    if (element === 'Ag_FA') {
      if (num < 200) return '<200';
      return String(Math.round(num));
    }
    if (element === 'Ag') {
      if (num < 1) return '<1';
      return String(Math.round(num));
    }
    if (unit === 'ppm') {
      if (num < 1) return '<1';
      return String(Math.round(num));
    }
    return strVal;
  };

  
  const headerStyle = {
    font: { bold: true, size: 11, color: { argb: 'FFFFFFFF' }, name: 'Segoe UI' },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB8CCE4' } },
    alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
    border: {
      top: { style: 'thin', color: { argb: 'FF555555' } },
      bottom: { style: 'thin', color: { argb: 'FF555555' } },
      left: { style: 'thin', color: { argb: 'FF555555' } },
      right: { style: 'thin', color: { argb: 'FF555555' } }
    }
  };
  
  const dataStyle = {
    font: { size: 10, name: 'Segoe UI' },
    alignment: { horizontal: 'center', vertical: 'middle' },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } },
    border: {
      top: { style: 'hair', color: { argb: 'FFDDDDDD' } },
      bottom: { style: 'hair', color: { argb: 'FFDDDDDD' } },
      left: { style: 'hair', color: { argb: 'FFDDDDDD' } },
      right: { style: 'hair', color: { argb: 'FFDDDDDD' } }
    }
  };
  
  const emptyDataStyle = {
    ...dataStyle,
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD3D3D3' } }
  };
  
  const titleLastCol = String.fromCharCode(64 + allElements.length + 2);
  qaqcSheet.mergeCells(`A1:${titleLastCol}1`);
  qaqcSheet.getCell('A1').value = '2. QA/QC Results';
  qaqcSheet.getCell('A1').style = {
    font: { bold: true, size: 14, color: { argb: 'FF000000' }, name: 'Segoe UI' },
    alignment: { horizontal: 'left', vertical: 'middle' }
  };
  qaqcSheet.getRow(1).height = 25;
  
  qaqcSheet.mergeCells('A3:B3');
  qaqcSheet.getCell('A3').value = 'METHOD';
  qaqcSheet.getCell('A3').style = headerStyle;
  allElements.forEach((el, idx) => {
    const cell = qaqcSheet.getCell(3, idx + 3);
    cell.value = methodHeaders[idx];
    cell.style = headerStyle;
  });
  qaqcSheet.getRow(3).height = 25;
  
  qaqcSheet.mergeCells('A4:B4');
  qaqcSheet.getCell('A4').value = 'LIMITE DETECTION';
  qaqcSheet.getCell('A4').style = headerStyle;
  allElements.forEach((el, idx) => {
    const cell = qaqcSheet.getCell(4, idx + 3);
    cell.value = limitHeaders[idx];
    cell.style = headerStyle;
  });
  qaqcSheet.getRow(4).height = 25;
  
  qaqcSheet.getCell('A5').value = 'Customers-Ref';
  qaqcSheet.getCell('A5').style = headerStyle;
  qaqcSheet.getCell('B5').value = 'Lab-Ref';
  qaqcSheet.getCell('B5').style = headerStyle;
  allElements.forEach((el, idx) => {
    const cell = qaqcSheet.getCell(5, idx + 3);
    cell.value = `${el}\n${elementUnits[el]}`;
    cell.style = headerStyle;
  });
  qaqcSheet.getRow(5).height = 30;
  
  qaqcData.reference_standards.forEach((standard: any, rowIdx: number) => {
    const rowNum = 6 + rowIdx;

    const labRef = (standard.lab_ref || '').toString().trim();
    const resolvedExternalId = labRef ? sampleExternalIdByLabRef[labRef] : undefined;
    const displayCustomersRef = (labRef && resolvedExternalId !== undefined && resolvedExternalId !== '')
      ? resolvedExternalId
      : (standard.customers_ref || '');

    const custCell = qaqcSheet.getCell(rowNum, 1);
    custCell.value = displayCustomersRef || '';
    custCell.style = (!displayCustomersRef || displayCustomersRef.toString().trim() === '')
                     ? emptyDataStyle : dataStyle;

    const labCell = qaqcSheet.getCell(rowNum, 2);
    labCell.value = standard.lab_ref || '';
    labCell.style = (!standard.lab_ref || standard.lab_ref.trim() === '')
                    ? emptyDataStyle : dataStyle;

    allElements.forEach((el, colIdx) => {
      const cell = qaqcSheet.getCell(rowNum, colIdx + 3);
      const rawValue = standard[el];
      const formatted = formatQaqcValue(rawValue, el);
      cell.value = formatted;
      cell.style = (formatted === '') ? emptyDataStyle : dataStyle;
    });

    qaqcSheet.getRow(rowNum).height = 22;
  });
  
  const totalCols = allElements.length + 2;
  const lastColLetter = String.fromCharCode(64 + totalCols);
  const footerRow = 6 + qaqcData.reference_standards.length + 1;
  qaqcSheet.mergeCells(`A${footerRow}:${lastColLetter}${footerRow}`);
  const footerCell = qaqcSheet.getCell(`A${footerRow}`);
  const analystName = qaqcData.batch_info?.analyst_name || 'N/A';
  const analysisDate = qaqcData.batch_info?.analysis_date 
    ? new Date(qaqcData.batch_info.analysis_date).toLocaleDateString('en-GB') 
    : 'N/A';
  footerCell.value = `* QA/QC data validated by: ${analystName} on ${analysisDate}`;
  footerCell.style = {
    font: { italic: true, size: 9, name: 'Segoe UI' },
    alignment: { horizontal: 'left', vertical: 'middle' }
  };
  
  qaqcSheet.columns = [
    { width: 25 }, { width: 20 },
    ...allElements.map(() => ({ width: 12 }))
  ];
};

// 🏆 PREMIUM CORPORATE EXCEL: Ultra-Modern Professional Design with Advanced Styling & Data Extraction
export const generateProfessionalExcel = async (certificateData: any, certificateNumber: string) => {
  try {
    // Load ExcelJS library with full styling support
    if (!(window as any).ExcelJS) {
      await loadExcelJSLibrary();
    }

    const ExcelJS = (window as any).ExcelJS;
    
    // Create workbook with ExcelJS (full styling support)
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Rapport AA');
    
    // ===== EXTRACT REAL CLIENT DATA FROM METADATA =====

    
    // Extract client info from batch metadata or samples
    let clientInfo = {
      client_name: 'AFRILAB CLIENT',
      project_name: 'GEOLOGICAL ANALYSIS', 
      client_reference: 'SAMPLE ANALYSIS',
      order_number: '',
      receipt_date: new Date().toLocaleDateString('en-GB')
    };
    
    // Try to extract from batch metadata
    if (certificateData.batch_info) {
      const metadata = certificateData.batch_info;
      clientInfo = {
        client_name: metadata.client_name || metadata.clientName || clientInfo.client_name,
        project_name: metadata.project_name || metadata.projectName || metadata.project || clientInfo.project_name,
        client_reference: metadata.client_reference || metadata.clientReference || metadata.reference || clientInfo.client_reference,
        order_number: metadata.order_number || metadata.orderNumber || metadata.order || clientInfo.order_number,
        receipt_date: metadata.receipt_date || metadata.receiptDate || metadata.date_reception || clientInfo.receipt_date
      };
    }
    
    // Try to extract from samples metadata  
    if (certificateData.samples && certificateData.samples.length > 0) {
      const firstSample = certificateData.samples[0];
      if (firstSample.metadata || firstSample.batch_info) {
        const sampleMeta = firstSample.metadata || firstSample.batch_info;
        clientInfo = {
          client_name: sampleMeta.client_name || sampleMeta.clientName || clientInfo.client_name,
          project_name: sampleMeta.project_name || sampleMeta.projectName || sampleMeta.project || clientInfo.project_name,
          client_reference: sampleMeta.client_reference || sampleMeta.clientReference || sampleMeta.reference || clientInfo.client_reference,
          order_number: sampleMeta.order_number || sampleMeta.orderNumber || sampleMeta.order || clientInfo.order_number,
          receipt_date: sampleMeta.receipt_date || sampleMeta.receiptDate || sampleMeta.date_reception || clientInfo.receipt_date
        };
      }
    }
    

    
    // ===== PREMIUM CORPORATE STYLING (Based on 2024 Design Trends) =====
    
    const headerStyle = {
      font: { bold: true, size: 12, color: { argb: 'FFFFFFFF' }, name: 'Segoe UI' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4A90D9' } },
      alignment: { horizontal: 'center', vertical: 'middle', wrapText: true, indent: 1 },
      border: {
        top: { style: 'medium', color: { argb: 'FF2E5984' } },
        bottom: { style: 'medium', color: { argb: 'FF2E5984' } },
        left: { style: 'thin', color: { argb: 'FFAAAAAA' } },
        right: { style: 'thin', color: { argb: 'FFAAAAAA' } }
      }
    };
    
    const titleStyle = {
      font: { bold: true, size: 16, color: { argb: 'FF1F4E79' }, name: 'Segoe UI' },
      alignment: { horizontal: 'center', vertical: 'middle' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } }
    };
    
    const companyStyle = {
      font: { bold: true, size: 22, color: { argb: 'FF0F2C47' }, name: 'Segoe UI' },
      alignment: { horizontal: 'left', vertical: 'middle' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } }
    };
    
    const dataStyle = {
      font: { size: 11, name: 'Segoe UI' },
      alignment: { horizontal: 'center', vertical: 'middle', indent: 0.5 },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } },
      border: {
        top: { style: 'hair', color: { argb: 'FFE1E5E9' } },
        bottom: { style: 'hair', color: { argb: 'FFE1E5E9' } },
        left: { style: 'hair', color: { argb: 'FFE1E5E9' } },
        right: { style: 'hair', color: { argb: 'FFE1E5E9' } }
      }
    };
    
    const dataAlternateStyle = {
      font: { size: 11, name: 'Segoe UI' },
      alignment: { horizontal: 'center', vertical: 'middle', indent: 0.5 },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } },
      border: {
        top: { style: 'hair', color: { argb: 'FFE1E5E9' } },
        bottom: { style: 'hair', color: { argb: 'FFE1E5E9' } },
        left: { style: 'hair', color: { argb: 'FFE1E5E9' } },
        right: { style: 'hair', color: { argb: 'FFE1E5E9' } }
      }
    };
    
    const subtitleStyle = {
      font: { bold: true, size: 14, color: { argb: 'FF2F5597' }, name: 'Segoe UI' },
      alignment: { horizontal: 'center', vertical: 'middle' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } }
    };
    
    const clientHeaderStyle = {
      font: { bold: true, size: 11, color: { argb: 'FFFFFFFF' }, name: 'Segoe UI' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF6BAA75' } },
      alignment: { horizontal: 'center', vertical: 'middle', wrapText: true, indent: 1 },
      border: {
        top: { style: 'medium', color: { argb: 'FF4A7C59' } },
        bottom: { style: 'medium', color: { argb: 'FF4A7C59' } },
        left: { style: 'thin', color: { argb: 'FFAAAAAA' } },
        right: { style: 'thin', color: { argb: 'FFAAAAAA' } }
      }
    };
    
    // ===== BUILD BEAUTIFUL LAYOUT =====
    
    // Row 1: AFRILAB Company Header with Premium Corporate Style
    worksheet.mergeCells('A1:L1');
    const afrilab = worksheet.getCell('A1');
    afrilab.value = '✨ AFRILAB - African Laboratory for Mining and Environment ✨';
    afrilab.style = companyStyle;
    
    // Add subtle spacing row
    worksheet.getRow(2).height = 12;
    
    // Row 3: Report Form Title (moved down for better spacing)
    worksheet.mergeCells('D3:I3');
    const formTitle = worksheet.getCell('D3');
    formTitle.value = '🔬 FORMULAIRE REALISATION 🔬';
    formTitle.style = {
      font: { bold: true, size: 15, color: { argb: 'FF2F5597' }, name: 'Segoe UI' },
      alignment: { horizontal: 'center', vertical: 'middle' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F4F8' } },
      border: {
        top: { style: 'thin', color: { argb: 'FFCBD5E0' } },
        bottom: { style: 'thin', color: { argb: 'FFCBD5E0' } },
        left: { style: 'thin', color: { argb: 'FFCBD5E0' } },
        right: { style: 'thin', color: { argb: 'FFCBD5E0' } }
      }
    };
    
    // Row 5-6: Report Titles (adjusted row numbers)
    worksheet.mergeCells('D5:I5');
    const reportTitle1 = worksheet.getCell('D5');
    reportTitle1.value = '📊 RAPPORT D\'ANALYSE 📊';
    reportTitle1.style = subtitleStyle;
    
    worksheet.mergeCells('D6:I6');
    const reportTitle2 = worksheet.getCell('D6');
    reportTitle2.value = '📈 ANALYTICAL REPORT 📈';
    reportTitle2.style = subtitleStyle;
    
    // Row 6: Certificate Number with Premium Design Box
    worksheet.mergeCells('J6:L6');
    const certCell = worksheet.getCell('J6');
    certCell.value = `🏆 Certificat N° ${certificateNumber} 🏆`;
    certCell.style = {
      font: { bold: true, size: 12, color: { argb: 'FF0F2C47' }, name: 'Segoe UI' },
      alignment: { horizontal: 'center', vertical: 'middle' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFBBF24' } },
      border: {
        top: { style: 'thick', color: { argb: 'FFD97706' } },
        bottom: { style: 'thick', color: { argb: 'FFD97706' } },
        left: { style: 'thick', color: { argb: 'FFD97706' } },
        right: { style: 'thick', color: { argb: 'FFD97706' } }
      }
    };
    
    // Row 9-10: Report Info
    const dateCell = worksheet.getCell('A9');
    dateCell.value = 'Report issue date';
    dateCell.style = { font: { bold: true } };
    
    const dateCellValue = worksheet.getCell('C9');
    dateCellValue.value = new Date().toLocaleDateString('en-GB');
    
    const descCell = worksheet.getCell('A10');
    descCell.value = 'Please find below the results of analyzes.';
    
    // Build Zgounder report number: AM/QA/DM/RA/LZ02492/02/26/V01
    const firstSampleId = certificateData.samples?.[0]?.sample_data?.sample_id || 
                          certificateData.samples?.[0]?.sample_id || '';
    const lotMatch = firstSampleId.match(/(LZ)-?(\d+)-(\d+)/);
    const lotCode = lotMatch ? lotMatch[1] + lotMatch[2] : certificateNumber;
    const lotYear = lotMatch ? lotMatch[3] : new Date().getFullYear().toString().slice(-2);
    const submissionMonth = String(new Date().getMonth() + 1).padStart(2, '0');
    const reportNumber = `AM/QA/DM/RA/${lotCode}/${submissionMonth}/${lotYear}/V01`;
    
    // ===== CLIENT INFO TABLE - VERTICAL LAYOUT (Zgounder format) =====
    
    const infoLabelStyle = {
      font: { bold: true, size: 11, name: 'Segoe UI' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD6E4F0' } },
      alignment: { horizontal: 'left', vertical: 'middle' },
      border: {
        top: { style: 'thin', color: { argb: 'FF999999' } },
        bottom: { style: 'thin', color: { argb: 'FF999999' } },
        left: { style: 'thin', color: { argb: 'FF999999' } },
        right: { style: 'thin', color: { argb: 'FF999999' } }
      }
    };
    
    const infoValueStyle = {
      font: { size: 11, name: 'Segoe UI' },
      alignment: { horizontal: 'left', vertical: 'middle' },
      border: {
        top: { style: 'thin', color: { argb: 'FF999999' } },
        bottom: { style: 'thin', color: { argb: 'FF999999' } },
        left: { style: 'thin', color: { argb: 'FF999999' } },
        right: { style: 'thin', color: { argb: 'FF999999' } }
      }
    };
    
    const infoRows = [
      ['Report number', reportNumber],
      ['', ''],
      ['Company', clientInfo.client_name],
      ['Interlocutor', '-'],
      ['Client reference', clientInfo.client_reference],
      ['', ''],
      ['Order number', clientInfo.order_number || '-'],
      ['Imputation code', '-'],
      ['Date of receipt', clientInfo.receipt_date || '-'],
      ['Date of analysis', new Date().toLocaleDateString('en-GB')],
      ['Sampled by', 'Customer'],
      ['Warehouse', '-'],
      ['Prepared by', 'AFRILAB'],
    ];
    
    infoRows.forEach((row, i) => {
      const rowNum = 12 + i;
      worksheet.mergeCells(rowNum, 1, rowNum, 2);
      const labelCell = worksheet.getCell(rowNum, 1);
      labelCell.value = row[0];
      labelCell.style = infoLabelStyle;
      
      worksheet.mergeCells(rowNum, 3, rowNum, 6);
      const valueCell = worksheet.getCell(rowNum, 3);
      valueCell.value = row[1];
      valueCell.style = infoValueStyle;
    });
    
    // Comments row after info table
    const commentsRow = 12 + infoRows.length + 1;
    const commentsCell = worksheet.getCell(commentsRow, 1);
    commentsCell.value = 'Comments: R.A.S';
    commentsCell.style = { font: { bold: true, size: 11, color: { argb: 'FF2F5597' }, name: 'Segoe UI' } };
    
    // ===== ANALYSIS TABLE WITH PROFESSIONAL STYLING =====
    
    if (certificateData.certificate_type === 'BATCH' && certificateData.samples) {
      // Detect if this is a BGM batch
      const firstSample = certificateData.samples[0];
      const metadonnees = firstSample?.sample_data?.metadonnees || firstSample?.metadonnees;
      const isBGMBatch = metadonnees?.bgm_method_selected === true;
      
      // Check if any sample has a REAL (non-2.5, non-null) weight (mechanical preparation).
      const hasWeight = certificateHasMeaningfulWeight(certificateData);

      if (isBGMBatch) {
        // ===== BGM-SPECIFIC 25-COLUMN STRUCTURE =====
        
        // Define the fixed BGM column structure (25 columns: A-Y)
        const bgmColumns = [
          // Basic info (A-C)
          { key: 'Customers-Ref', header: 'Customers-Ref', method: '', limit: '', unit: '' },
          { key: 'Lab-Ref', header: 'Lab-Ref', method: '', limit: '', unit: '' },
          { key: 'LAB_WEIGHT', header: 'LAB_WEIGHT', method: '', limit: '', unit: 'Kg' },
          
          // SAA Elements (D-H) - HAS VALUES
          { key: 'Ag', header: 'Ag ', method: 'MO/ANA/002', limit: '1', unit: ' g/t' },
          { key: 'Cu', header: 'Cu', method: 'MO/ANA/002', limit: '0.01', unit: ' %' },
          { key: 'Fe', header: 'Fe ', method: 'MO/ANA/002', limit: '0.01', unit: '%' },
          { key: 'Pb', header: 'Pb ', method: 'MO/ANA/002', limit: '0.01', unit: '%' },
          { key: 'Zn', header: 'Zn', method: 'MO/ANA/002', limit: '0.01', unit: ' %' },
          
          // SAA Check Elements (I-M) - EMPTY
          { key: 'Ag_CK', header: 'Ag_CK ', method: 'MO/ANA/002', limit: '1', unit: ' g/t' },
          { key: 'Cu_CK', header: 'Cu_CK', method: 'MO/ANA/002', limit: '0.01', unit: ' %' },
          { key: 'Fe_CK', header: 'Fe_CK', method: 'MO/ANA/002', limit: '0.01', unit: '%' },
          { key: 'Pb_CK', header: 'Pb_CK', method: 'MO/ANA/002', limit: '0.01', unit: '%' },
          { key: 'Zn_CK', header: 'Zn_CK', method: 'MO/ANA/002', limit: '0.01', unit: ' %' },
          
          // Fire Assay Silver (N-O) - EMPTY
          { key: 'Ag_FA', header: 'Ag_FA', method: 'MO/ANA/035', limit: '200', unit: ' g/t' },
          { key: 'Ag_FA_CK', header: 'Ag_FA_CK', method: 'MO/ANA/035', limit: '200', unit: ' g/t' },
          
          // Fire Assay Gold (P) - HAS VALUES
          { key: 'Au', header: 'Au_FA', method: 'MO/ANA/001', limit: '0.05', unit: ' g/t' },
          
          // ICP Elements (Q-S) - HAS VALUES
          { key: 'Mo', header: 'Mo_ICP', method: 'MO/ANA/038', limit: '1', unit: 'ppm' },
          { key: 'As', header: 'As_ICP', method: 'MO/ANA/038', limit: '1', unit: 'ppm' },
          { key: 'Sn', header: 'Sn_ICP', method: 'MO/ANA/038', limit: '1', unit: 'ppm' },
          
          // Gravimetric Gold (T-V) - EMPTY
          { key: 'Au_GRAV', header: 'Au_GRAV', method: 'MO/ANA/001', limit: '100', unit: ' g/t' },
          { key: 'Au_GRAV_CK', header: 'Au_GRAV_CK', method: 'MO/ANA/001', limit: '100', unit: ' g/t' },
          { key: 'Au_FA_CK', header: 'Au_FA_CK', method: 'MO/ANA/001', limit: '0.05', unit: ' g/t' },
          
          // ICP Check Elements (W-Y) - EMPTY
          { key: 'Mo_ICP_CK', header: 'Mo_ICP_CK', method: 'MO/ANA/038', limit: '1', unit: 'ppm' },
          { key: 'As_ICP_CK', header: 'As_ICP_CK', method: 'MO/ANA/038', limit: '1', unit: 'ppm' },
          { key: 'Sn_ICP_CK', header: 'Sn_ICP_CK', method: 'MO/ANA/038', limit: '1', unit: 'ppm' }
        ];
        
        // Row 15: METHOD
        worksheet.mergeCells('A15:C15');
        const methodCell = worksheet.getCell('A15');
        methodCell.value = 'METHOD';
        methodCell.style = headerStyle;
        
        bgmColumns.forEach((col, i) => {
          if (i >= 3) { // Skip A-C (basic info columns)
            const cell = worksheet.getCell(15, i + 1);
            cell.value = col.method;
            cell.style = headerStyle;
          }
        });
        
        // Row 16: LIMITE DETECTION
        worksheet.mergeCells('A16:C16');
        const limitsCell = worksheet.getCell('A16');
        limitsCell.value = 'LIMITE DETECTION';
        limitsCell.style = headerStyle;
        
        bgmColumns.forEach((col, i) => {
          if (i >= 3) { // Skip A-C
            const cell = worksheet.getCell(16, i + 1);
            cell.value = col.limit;
            cell.style = headerStyle;
          }
        });
        
        // Row 17: Headers
        effectiveBgmColumns.forEach((col, i) => {
          const cell = worksheet.getCell(17, i + 1);
          cell.value = col.header;
          cell.style = headerStyle;
        });
        
        // Row 18: Units
        effectiveBgmColumns.forEach((col, i) => {
          const cell = worksheet.getCell(18, i + 1);
          cell.value = col.unit;
          cell.style = headerStyle;
        });
        
        // Sample Data Rows
        certificateData.samples.forEach((sample: any, index: number) => {
          const rowNum = 19 + index;
          const isEvenRow = index % 2 === 0;
          const currentDataStyle = isEvenRow ? dataStyle : dataAlternateStyle;
          
          const receptionWeight = sample.sample_data?.metadonnees?.poids_kg || 
                                 sample.metadonnees?.poids_kg || 
                                 sample.weight || 0.5;
          
          // Build row data according to BGM structure
          const sampleData: any[] = [];
          
          effectiveBgmColumns.forEach((col) => {
            if (col.key === 'Customers-Ref') {
              sampleData.push(sample.sample_data?.external_id || sample.client_ref || sample.external_id || `D-${195 + index}`);
            } else if (col.key === 'Lab-Ref') {
              sampleData.push(sample.sample_data?.sample_id || sample.lab_ref || sample.sample_id || `AFL-${certificateNumber}-${String(index + 1).padStart(2, '0')}`);
            } else if (col.key === 'LAB_WEIGHT') {
              sampleData.push(parseFloat(receptionWeight).toFixed(4));
            } else if (col.key === 'Ag') {
              // 🎯 BGM FEATURE: Ag superior limit - show ">200" when value exceeds 200 g/t
              const result = sample.results?.find((r: any) => r.element === 'Ag');
              if (result) {
                const concentration = parseFloat(result.concentration || 0);
                if (concentration > 200) {
                  sampleData.push('>200'); // Superior limit exceeded
                } else {
                  const formatted = result.formatted_concentration;
                  if (formatted !== undefined && formatted !== null) {
                    sampleData.push(String(formatted));
                  } else {
                    sampleData.push(Number.isFinite(concentration) ? concentration.toFixed(2) : 'N/A');
                  }
                }
              } else {
                sampleData.push('');
              }
            } else if (col.key === 'Ag_FA') {
              // 🎯 BGM FEATURE: Ag_FA shows actual value ONLY when Ag > 200 (avoid redundancy)
              const agResult = sample.results?.find((r: any) => r.element === 'Ag');
              if (agResult) {
                const concentration = parseFloat(agResult.concentration || 0);
                if (concentration > 200) {
                  // Show actual Ag value (since Ag column shows ">200")
                  const formatted = agResult.formatted_concentration;
                  if (formatted !== undefined && formatted !== null) {
                    sampleData.push(String(formatted));
                  } else {
                    sampleData.push(Number.isFinite(concentration) ? concentration.toFixed(2) : 'N/A');
                  }
                } else {
                  // Empty cell (value already shown in Ag column)
                  sampleData.push('');
                }
              } else {
                sampleData.push('');
              }
            } else if (col.key.endsWith('_CK') || col.key === 'Au_GRAV' || col.key === 'Au_GRAV_CK' || col.key === 'Au_FA_CK') {
              // Empty check columns
              sampleData.push('');
            } else {
              // Data columns: Cu, Fe, Pb, Zn, Au, Mo, As, Sn
              const result = sample.results?.find((r: any) => r.element === col.key);
              if (result) {
                const formatted = result.formatted_concentration;
                if (formatted !== undefined && formatted !== null) {
                  sampleData.push(String(formatted));
                } else {
                  const concentration = parseFloat(result.concentration || 0);
                  sampleData.push(Number.isFinite(concentration) ? concentration.toFixed(2) : 'N/A');
                }
              } else {
                sampleData.push('');
              }
            }
          });
          
          worksheet.getRow(rowNum).height = 24;
          
          sampleData.forEach((value, i) => {
            const cell = worksheet.getCell(rowNum, i + 1);
            cell.value = value;
            cell.style = currentDataStyle;
          });
        });
        
      } else {
        // ===== NON-BGM BATCHES: ZGOUNDER DYNAMIC BEHAVIOR =====
        
        const elements: string[] = Array.isArray(certificateData.elements) ? certificateData.elements : [];
        const tableStartRow = commentsRow + 3;
        
        // "Please find below the results of analyses:" label
        const resultsLabelRow = tableStartRow - 1;
        worksheet.getCell(resultsLabelRow, 1).value = '1. Please find below the results of analyses:';
        worksheet.getCell(resultsLabelRow, 1).style = { font: { bold: true, size: 12, name: 'Segoe UI' } };
        
        // METHOD row
        const colOffset = hasWeight ? 4 : 3;
        worksheet.mergeCells(tableStartRow, 1, tableStartRow, colOffset - 1);
        const methodCell = worksheet.getCell(tableStartRow, 1);
        methodCell.value = 'METHOD';
        methodCell.style = headerStyle;
      
        const firstSample = certificateData.samples[0];
        elements.forEach((elementName: string, i) => {
          const cell = worksheet.getCell(tableStartRow, i + colOffset);
          const res = firstSample?.results?.find((r: any) => r.element === elementName);
          let code = res?.method_code || 'PRE.MO/ANA/002';
          code = code.replace(/^AA-/, '');
          cell.value = code;
          cell.style = headerStyle;
        });
      
        // LIMITE DETECTION row
        const limRow = tableStartRow + 1;
        worksheet.mergeCells(limRow, 1, limRow, colOffset - 1);
        const limitsCell = worksheet.getCell(limRow, 1);
        limitsCell.value = 'LIMITE DETECTION';
        limitsCell.style = headerStyle;
      
        elements.forEach((elementName: string, i) => {
          const cell = worksheet.getCell(limRow, i + colOffset);
          const res = firstSample?.results?.find((r: any) => r.element === elementName);
          const dl = res?.detection_limit;
          if (dl && typeof dl === 'object' && dl.limit !== undefined) {
            const unit = res?.unit || dl.unit || 'ppm';
            if (unit === 'g/t') {
              cell.value = dl.limit;
            } else {
              cell.value = dl.limitInPercent || (dl.limit / 10000) || 0.01;
            }
          } else if (typeof dl === 'number') {
            cell.value = dl;
          } else {
            cell.value = elementName === 'Ag' || elementName === 'Au' ? 1 : 0.01;
          }
          cell.style = headerStyle;
        });
      
        // Column Headers row (Customers-Ref, Lab-Ref, LAB_WEIGHT, elements*)
        const hdrRow = tableStartRow + 2;
        const headers = hasWeight ? ['Customers-Ref', 'Lab-Ref', 'LAB_WEIGHT'] : ['Customers-Ref', 'Lab-Ref'];
        elements.forEach((element: string) => headers.push(element + '*'));
      
        headers.forEach((header, i) => {
          const cell = worksheet.getCell(hdrRow, i + 1);
          cell.value = header;
          cell.style = headerStyle;
        });
      
        // Units row
        const unitRow = tableStartRow + 3;
        const units = hasWeight ? ['', '', 'Kg'] : ['', ''];
        elements.forEach((elementName: string) => {
          if (elementName === 'Ag' || elementName === 'Au') {
            units.push('g/t');
          } else {
            units.push('%');
          }
        });
      
        units.forEach((unit, i) => {
          const cell = worksheet.getCell(unitRow, i + 1);
          cell.value = unit;
          cell.style = headerStyle;
        });
      
        // Sample Data Rows
        const dataStartRow = tableStartRow + 4;
        certificateData.samples.forEach((sample: any, index: number) => {
          const rowNum = dataStartRow + index;
          const isEvenRow = index % 2 === 0;
          const currentDataStyle = isEvenRow ? dataStyle : dataAlternateStyle;
          
          // 🔧 CRITICAL FIX: Use actual reception weight from metadonnees (exactly like HTML)
          const receptionWeight = sample.sample_data?.metadonnees?.poids_kg || 
                                 sample.metadonnees?.poids_kg || 
                                 sample.weight;
          
          const sampleData: any[] = [
            sample.sample_data?.external_id || sample.client_ref || sample.external_id || `D-${195 + index}`,
            sample.sample_data?.sample_id || sample.lab_ref || sample.sample_id || `AFL-${certificateNumber}-${String(index + 1).padStart(2, '0')}`,
          ];
          if (hasWeight) {
            sampleData.push(parseFloat(receptionWeight || 0).toFixed(4));
          }
          
          // 🔧 FIX: Get this sample's selected elements to check if element should show "-"
          const sampleSelectedElements = sample.sample_data?.metadonnees?.selected_elements || 
                                         sample.metadonnees?.selected_elements || [];
          
          elements.forEach((element: string) => {
            // 🔧 FIX: Check if element is in this sample's selected_elements
            const hasElement = sampleSelectedElements.includes(element);
            
            if (!hasElement) {
              // Element not selected for this sample - show "-"
              sampleData.push('-');
              return;
            }
            
            const result = sample.results?.find((r: any) => r.element === element);
            if (result) {
              // Prefer backend-provided formatted value which already applies method-specific detection limits
              const formatted = result.formatted_concentration;
              if (formatted !== undefined && formatted !== null) {
                sampleData.push(String(formatted));
              } else {
                // Fallback to raw concentration with minimal formatting
                const concentration = parseFloat(result.concentration || 0);
                sampleData.push(Number.isFinite(concentration) ? concentration.toFixed(2) : 'N/A');
              }
            } else {
              // No result found - show a generic below-detection indicator
              sampleData.push('');
            }
          });
          
          // Apply premium styling with generous row height
          worksheet.getRow(rowNum).height = 24; // Much taller data rows
          
          sampleData.forEach((value, i) => {
            const cell = worksheet.getCell(rowNum, i + 1);
            cell.value = value;
            cell.style = currentDataStyle;
          });
        });
      }
    } else if (certificateData.certificate_type === 'INDIVIDUAL') {
      const elements: string[] = Array.isArray(certificateData.elements) ? certificateData.elements : [];
      const results: any[] = Array.isArray(certificateData.results) ? certificateData.results : [];

      const hasWeightIndiv = certificateHasMeaningfulWeight(certificateData);
      const indColOffset = hasWeightIndiv ? 4 : 3;

      // Method row
      worksheet.mergeCells(15, 1, 15, indColOffset - 1);
      const methodCell = worksheet.getCell('A15');
      methodCell.value = 'METHOD';
      methodCell.style = headerStyle;
      elements.forEach((el, i) => {
        const cell = worksheet.getCell(15, i + indColOffset);
        const res = results.find(r => r.element === el);
        cell.value = res?.method_code || '';
        cell.style = headerStyle;
      });

      // Detection limits row
      worksheet.mergeCells(16, 1, 16, indColOffset - 1);
      const limitsCell = worksheet.getCell('A16');
      limitsCell.value = 'LIMITE DETECTION';
      limitsCell.style = headerStyle;
      elements.forEach((el, i) => {
        const cell = worksheet.getCell(16, i + indColOffset);
        const res = results.find(r => r.element === el);
        const limit = res?.detection_limit;
        cell.value = typeof limit === 'number' ? limit : '';
        cell.style = headerStyle;
      });

      // Column headers
      const headers = hasWeightIndiv ? ['Customers-Ref', 'Lab-Ref', 'LAB_WEIGHT'] : ['Customers-Ref', 'Lab-Ref'];
      elements.forEach((el: string) => headers.push(el + '*'));
      headers.forEach((header, i) => {
        const cell = worksheet.getCell(17, i + 1);
        cell.value = header;
        cell.style = headerStyle;
      });

      // Units row
      const units = hasWeightIndiv ? ['', '', 'Kg'] : ['', ''];
      elements.forEach((el: string) => {
        const res = results.find(r => r.element === el);
        units.push(res?.unit || '');
      });
      units.forEach((unit, i) => {
        const cell = worksheet.getCell(18, i + 1);
        cell.value = unit;
        cell.style = headerStyle;
      });

      // Data row
      const rowNum = 19;
      worksheet.getRow(rowNum).height = 24;
      const sampleRow: any[] = [
        certificateData.client_ref || 'N/A',
        certificateData.lab_ref || 'N/A',
      ];
      if (hasWeightIndiv) {
        sampleRow.push((certificateData.sample_data?.metadonnees?.poids_kg || 0).toFixed(4));
      }
      elements.forEach((el: string) => {
        const res = results.find(r => r.element === el);
        if (res) {
          const formatted = res.formatted_concentration;
          sampleRow.push(formatted != null ? String(formatted) : (Number.isFinite(+res.concentration) ? Number(res.concentration).toFixed(2) : 'N/A'));
        } else {
          sampleRow.push('');
        }
      });
      sampleRow.forEach((value, i) => {
        const cell = worksheet.getCell(rowNum, i + 1);
        cell.value = value as any;
        cell.style = dataStyle;
      });
    }
    
    // ===== PREMIUM SPACING & LAYOUT (Extra Wide & Tall) =====
    
    worksheet.columns = [
      { width: 25 }, // A: Customer-Ref (much wider for premium feel)
      { width: 22 }, // B: Lab-Ref (much wider for premium feel)
      ...(hasWeight || !certificateData.samples ? [{ width: 15 }] : []), // C: LAB_WEIGHT (wider, only if weight exists)
      { width: 12 }, // D: Analysis columns (more spacious)
      { width: 12 }, // E: Analysis columns (more spacious)
      { width: 12 }, // F: Analysis columns (more spacious)
      { width: 12 }, // G: Analysis columns (more spacious)
      { width: 12 }, // H: Analysis columns (more spacious)
      { width: 12 }, // I: Analysis columns (more spacious)
      { width: 12 }, // J: Analysis columns (more spacious)
      { width: 12 }, // K: Analysis columns (more spacious)
      { width: 25 }  // L: Certificate column (much wider)
    ];
    
    // Set premium row heights with generous spacing
    worksheet.getRow(1).height = 45;  // AFRILAB header - extra prominent
    worksheet.getRow(2).height = 35;  // Form title - spacious
    worksheet.getRow(3).height = 20;  // More spacing
    worksheet.getRow(4).height = 30;  // Report titles - taller
    worksheet.getRow(5).height = 30;  // Report titles - taller
    worksheet.getRow(6).height = 28;  // Certificate number - premium height
    worksheet.getRow(7).height = 20;  // More spacing
    worksheet.getRow(8).height = 20;  // More spacing
    worksheet.getRow(9).height = 25;  // Date - taller
    worksheet.getRow(10).height = 25; // Description - taller
    worksheet.getRow(11).height = 20; // More spacing
    worksheet.getRow(12).height = 32; // Client headers - much taller
    worksheet.getRow(13).height = 28; // Client data - taller
    // Row heights are set dynamically based on tableStartRow in the results section
    
    // ===== ADD QA/QC WORKSHEET IF DATA EXISTS =====
    if (hasQAQCData(certificateData)) {
      generateQAQCWorksheet(workbook, certificateData, ExcelJS);
    }

    // ===== ADD ANNEX WORKSHEET: REFERENCE OF METHODS =====
    
    const annexSheet = workbook.addWorksheet('Annexe');
    
    annexSheet.columns = [
      { width: 5 },
      { width: 25 },
      { width: 55 },
    ];
    
    const annexHeaderStyle = {
      font: { bold: true, size: 14, color: { argb: 'FF2F5597' }, name: 'Segoe UI' },
      alignment: { horizontal: 'center', vertical: 'middle' },
    };
    
    const annexTableHeaderStyle = {
      font: { bold: true, size: 12, color: { argb: 'FF000000' }, name: 'Segoe UI' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD6E4F0' } },
      alignment: { horizontal: 'center', vertical: 'middle' },
      border: {
        top: { style: 'thin', color: { argb: 'FF999999' } },
        bottom: { style: 'thin', color: { argb: 'FF999999' } },
        left: { style: 'thin', color: { argb: 'FF999999' } },
        right: { style: 'thin', color: { argb: 'FF999999' } },
      },
    };
    
    const annexCellStyle = {
      font: { size: 11, name: 'Segoe UI' },
      alignment: { horizontal: 'center', vertical: 'middle' },
      border: {
        top: { style: 'thin', color: { argb: 'FF999999' } },
        bottom: { style: 'thin', color: { argb: 'FF999999' } },
        left: { style: 'thin', color: { argb: 'FF999999' } },
        right: { style: 'thin', color: { argb: 'FF999999' } },
      },
    };
    
    annexSheet.mergeCells('A2:C2');
    annexSheet.getCell('A2').value = '2. ANNEX:';
    annexSheet.getCell('A2').style = { font: { bold: true, size: 13, underline: true, name: 'Segoe UI' } };
    
    annexSheet.mergeCells('A4:C4');
    annexSheet.getCell('A4').value = 'REFERENCE OF METHODS';
    annexSheet.getCell('A4').style = { font: { bold: true, size: 13, underline: true, name: 'Segoe UI' }, alignment: { horizontal: 'center' } };
    
    annexSheet.getCell('B6').value = 'Method';
    annexSheet.getCell('B6').style = annexTableHeaderStyle;
    annexSheet.getCell('C6').value = 'Description';
    annexSheet.getCell('C6').style = annexTableHeaderStyle;
    
    annexSheet.getCell('B7').value = 'PRE.MO/ANA/015';
    annexSheet.getCell('B7').style = { ...annexCellStyle, font: { bold: true, size: 11, name: 'Segoe UI' } };
    annexSheet.getCell('C7').value = 'Mechanical preparation';
    annexSheet.getCell('C7').style = annexCellStyle;
    
    annexSheet.getCell('B8').value = 'PRE.MO/ANA/002';
    annexSheet.getCell('B8').style = { ...annexCellStyle, font: { bold: true, size: 11, name: 'Segoe UI' } };
    annexSheet.getCell('C8').value = 'Ag, Cu, Fe, Pb and Zn by AAS Analysis';
    annexSheet.getCell('C8').style = annexCellStyle;
    
    annexSheet.getRow(6).height = 30;
    annexSheet.getRow(7).height = 30;
    annexSheet.getRow(8).height = 30;
    
    // ===== GENERATE AND DOWNLOAD PREMIUM CORPORATE EXCEL =====
    
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${buildCertificateFilename({ ...certificateData, certificate_number: certificateNumber })}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
    
    
    
  } catch (error) {
    console.error('Excel generation error:', error);
    throw new Error('Erreur lors de la génération du fichier Excel professionnel');
  }

};

// 🏆 PREMIUM PDF EXPORT: Ultra-Modern Design with Corporate Styling
export const exportToPremiumPDF = async (certificateId: string, certificateNumber: string, token: string) => {
  try {
    const response = await fetch(`/api/v1/certificates/${certificateId}/download`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch certificate');
    }
    
    const htmlContent = await response.text();
    
    // Open premium PDF window with enhanced styling
    const pdfWindow = window.open('', '_blank');
    if (!pdfWindow) {
      throw new Error('Popup blocked - Please allow popups for this site');
    }
    
    pdfWindow.document.open();
    pdfWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>🏆 PDF Premium - ${certificateNumber}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
            line-height: 1.6;
            color: #2C3E50;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            min-height: 100vh;
          }
          
          .pdf-container {
            max-width: 1200px;
            margin: 0 auto;
            background: white;
            box-shadow: 0 20px 60px rgba(0,0,0,0.1);
            border-radius: 15px;
            overflow: hidden;
          }
          
          .pdf-header {
            background: linear-gradient(135deg, #2E5984 0%, #4A90D9 100%);
            color: white;
            padding: 30px;
            text-align: center;
            position: relative;
            overflow: hidden;
          }
          
          .pdf-header::before {
            content: '';
            position: absolute;
            top: -50%;
            left: -50%;
            width: 200%;
            height: 200%;
            background: repeating-linear-gradient(
              45deg,
              transparent,
              transparent 10px,
              rgba(255,255,255,0.05) 10px,
              rgba(255,255,255,0.05) 20px
            );
            animation: slide 20s linear infinite;
          }
          
          @keyframes slide {
            0% { transform: translateX(-50px) translateY(-50px); }
            100% { transform: translateX(50px) translateY(50px); }
          }
          
          .pdf-header h1 {
            font-size: 28px;
            font-weight: 700;
            margin-bottom: 10px;
            position: relative;
            z-index: 2;
          }
          
          .pdf-header p {
            font-size: 16px;
            opacity: 0.9;
            position: relative;
            z-index: 2;
          }
          
          .pdf-content {
            padding: 40px;
          }
          
          .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 400px;
            text-align: center;
          }
          
          .spinner {
            width: 60px;
            height: 60px;
            border: 4px solid #f3f3f3;
            border-top: 4px solid #4A90D9;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 30px;
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          .loading-text {
            font-size: 18px;
            font-weight: 600;
            color: #2E5984;
            margin-bottom: 20px;
          }
          
          .instructions {
            background: linear-gradient(135deg, #E8F4FD 0%, #F0F8FF 100%);
            padding: 25px;
            border-radius: 15px;
            border-left: 5px solid #4A90D9;
            margin: 20px 0;
            max-width: 500px;
          }
          
          .instructions h4 {
            color: #2E5984;
            margin-bottom: 15px;
            font-size: 16px;
          }
          
          .instructions ol {
            margin-left: 20px;
            margin-bottom: 15px;
          }
          
          .instructions li {
            margin-bottom: 8px;
            color: #34495E;
          }
          
          .instructions strong {
            color: #2E5984;
          }
          
          .button {
            background: linear-gradient(135deg, #4A90D9 0%, #2E5984 100%);
            color: white;
            border: none;
            padding: 15px 30px;
            border-radius: 25px;
            cursor: pointer;
            font-size: 16px;
            font-weight: 600;
            margin: 10px;
            box-shadow: 0 4px 15px rgba(74, 144, 217, 0.3);
            transition: all 0.3s ease;
          }
          
          .button:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(74, 144, 217, 0.4);
          }
          
          .button.secondary {
            background: linear-gradient(135deg, #95A5A6 0%, #7F8C8D 100%);
            box-shadow: 0 4px 15px rgba(149, 165, 166, 0.3);
          }
          
          .button.secondary:hover {
            box-shadow: 0 6px 20px rgba(149, 165, 166, 0.4);
          }
          
          .pdf-footer {
            background: linear-gradient(135deg, #ECF0F1 0%, #D5DBDB 100%);
            padding: 25px;
            text-align: center;
            border-top: 3px solid #4A90D9;
            margin-top: 40px;
          }
          
          .pdf-footer p {
            margin: 8px 0;
            color: #5D6D7E;
            font-size: 14px;
          }
          
          /* Print styles */
          @media print {
            body {
              background: white;
            }
            .pdf-container {
              box-shadow: none;
              border-radius: 0;
            }
            .loading-container {
              display: none;
            }
          }
          
          /* Enhanced table styling */
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
            border-radius: 10px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.1);
          }
          
          th {
            background: linear-gradient(135deg, #4A90D9 0%, #2E5984 100%);
            color: white;
            padding: 15px;
            text-align: left;
            font-weight: 600;
            font-size: 14px;
          }
          
          td {
            padding: 12px 15px;
            border-bottom: 1px solid #E8F4FD;
            font-size: 13px;
          }
          
          tr:nth-child(even) {
            background-color: #F8FBFF;
          }
          
          tr:hover {
            background-color: #E8F4FD;
          }
        </style>
      </head>
      <body>
        <div class="pdf-container">
          <div class="pdf-header">
            <h1>🏆 AFRILAB - Certificat Premium</h1>
            <p>Laboratoire Africain pour l'Exploitation Minière et l'Environnement</p>
          </div>
          
          <div class="pdf-content">
            <div class="loading-container">
              <div class="spinner"></div>
              <div class="loading-text">🔄 Préparation de votre certificat premium en cours...</div>
              <div class="instructions">
                <h4>📄 Pour sauvegarder en PDF Premium :</h4>
                <ol>
                  <li>Utilisez <strong>Ctrl+P</strong> (ou Cmd+P sur Mac)</li>
                  <li>Sélectionnez <strong>"Enregistrer au format PDF"</strong></li>
                  <li>Choisissez la destination et cliquez <strong>"Enregistrer"</strong></li>
                </ol>
                <p><strong>💡 Avantages Premium :</strong> Design moderne, couleurs professionnelles, références automatiques sur chaque page, et styling corporate élégant.</p>
              </div>
              <button class="button" onclick="window.print()">🖨️ Imprimer/Sauvegarder PDF Premium</button>
              <button class="button secondary" onclick="window.close()">❌ Fermer</button>
            </div>
          </div>
        </div>
        
        <script>
          // Load the actual certificate content
          setTimeout(() => {
            document.querySelector('.loading-container').style.display = 'none';
            document.querySelector('.pdf-content').innerHTML = \`${htmlContent}\`;
          }, 1500);
        </script>
      </body>
      </html>
    `);
    pdfWindow.document.close();
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    throw error;
  }
};
